import React, { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/api/client";
import AuthImage from "@/components/AuthImage";
import AuthVideo from "@/components/AuthVideo";
import ImageLightbox from "@/components/ImageLightbox";
import { Input } from "@/components/ui/input";
import { LifeBuoy, Search, GraduationCap, BookOpen, HelpCircle, ChevronDown, ImageIcon, ZoomIn, Download, Loader2 } from "lucide-react";

const SECTION_META = {
  training: { label: "Formations", icon: GraduationCap, accent: "#0ea5e9" },
  guide: { label: "Notices", icon: BookOpen, accent: "#023E73" },
  faq: { label: "FAQ", icon: HelpCircle, accent: "#F97316" },
};

async function downloadPdf(url, filename, onDone) {
  try {
    const token = localStorage.getItem("wrm_token");
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error();
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(objUrl);
  } catch {
    toast.error("Échec du téléchargement du PDF");
  } finally {
    onDone();
  }
}

function ArticleMedia({ article }) {
  const images = article.images || [];
  const videos = article.videos || [];
  const [lightbox, setLightbox] = useState(null);
  if (images.length === 0 && videos.length === 0) return null;
  return (
    <div className="mt-4 space-y-3">
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid={`help-article-images-${article.id}`}>
          {images.map((img) => (
            <button
              key={img.id}
              type="button"
              data-testid={`help-article-image-${img.id}`}
              onClick={() => setLightbox({ src: img.url, alt: img.name })}
              className="relative rounded-lg border border-slate-200 shadow-sm overflow-hidden bg-slate-50 group cursor-zoom-in"
            >
              <AuthImage src={img.url} alt={img.name} className="w-full h-32 object-cover" fallback={<div className="h-32 flex items-center justify-center text-slate-300"><ImageIcon size={20} /></div>} />
              <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <ZoomIn size={18} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </span>
            </button>
          ))}
        </div>
      )}
      {videos.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3" data-testid={`help-article-videos-${article.id}`}>
          {videos.map((v) => (
            <div key={v.id} className="rounded-lg border border-slate-200 shadow-sm overflow-hidden">
              <AuthVideo src={v.url} className="aspect-video" />
            </div>
          ))}
        </div>
      )}
      {lightbox && (
        <ImageLightbox src={lightbox.src} alt={lightbox.alt} open={!!lightbox} onOpenChange={(v) => !v && setLightbox(null)} />
      )}
    </div>
  );
}

function ArticleCard({ article, defaultOpen, highlighted }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [downloading, setDownloading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (highlighted) {
      setOpen(true);
      ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlighted]);

  return (
    <div
      ref={ref}
      data-testid={`help-article-${article.id}`}
      className={`rounded-xl bg-white border overflow-hidden transition-all ${highlighted ? "border-[#023E73] ring-2 ring-[#023E73]/20 shadow-md" : "border-slate-200 hover:border-slate-300 hover:shadow-sm"}`}
    >
      <div className="w-full flex items-center gap-2 px-4 py-3.5">
        <button
          data-testid={`help-article-toggle-${article.id}`}
          onClick={() => setOpen(!open)}
          className="flex-1 flex items-center justify-between gap-3 text-left"
        >
          <span className="font-semibold text-sm text-slate-900">{article.title}</span>
          <ChevronDown size={16} className={`text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {article.section !== "faq" && (
          <button
            type="button"
            data-testid={`help-article-pdf-${article.id}`}
            title="Télécharger en PDF"
            disabled={downloading}
            onClick={() => { setDownloading(true); downloadPdf(`${API}/help-content/${article.id}/export-pdf`, `${article.title}.pdf`, () => setDownloading(false)); }}
            className="flex-shrink-0 p-1.5 rounded-md border border-slate-200 text-slate-500 hover:text-[#023E73] hover:border-[#023E73]/40 hover:bg-slate-50 transition-colors"
          >
            {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          </button>
        )}
      </div>
      {open && (
        <div className="px-4 pb-4 text-sm text-slate-600 whitespace-pre-line leading-relaxed border-t border-slate-100 pt-3">
          {article.content}
          <ArticleMedia article={article} />
        </div>
      )}
    </div>
  );
}

export default function HelpCenter() {
  const [search, setSearch] = useState("");
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [searchParams] = useSearchParams();
  const targetSlug = searchParams.get("article");
  const { data, isLoading } = useQuery({
    queryKey: ["help-content", "mine"],
    queryFn: () => api.get("/help-content/mine").then((r) => r.data),
  });

  const filtered = useMemo(() => {
    const articles = data?.articles || [];
    const q = search.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter((a) => a.title.toLowerCase().includes(q) || a.content.toLowerCase().includes(q));
  }, [data, search]);

  const grouped = useMemo(() => {
    const g = { training: [], guide: [], faq: [] };
    for (const a of filtered) { if (g[a.section]) g[a.section].push(a); }
    return g;
  }, [filtered]);

  const guideCount = (data?.articles || []).filter((a) => a.section !== "faq").length;

  return (
    <div className="space-y-8" data-testid="help-center-page">
      {/* Hero */}
      <div className="rounded-2xl bg-gradient-to-br from-[#023E73] to-[#012648] px-6 sm:px-10 py-10 sm:py-14 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 0, transparent 45%), radial-gradient(circle at 80% 70%, white 0, transparent 40%)" }} />
        <LifeBuoy size={30} className="text-white/80 mx-auto mb-3" />
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">Centre d&apos;aide</h1>
        <p className="text-sm sm:text-base text-white/70 mt-2">
          {data?.profile_label ? `Contenu personnalisé pour votre profil : ${data.profile_label}` : "Formations, notices et FAQ pour utiliser l'application en toute autonomie"}
        </p>
        <div className="relative max-w-xl mx-auto mt-6">
          <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="help-center-search"
            placeholder="Que recherchez-vous ?"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-11 h-12 rounded-full border-0 shadow-lg text-sm bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-white/60"
          />
        </div>
        {guideCount > 0 && (
          <button
            type="button"
            data-testid="help-center-download-all-pdf"
            disabled={downloadingAll}
            onClick={() => {
              setDownloadingAll(true);
              downloadPdf(`${API}/help-content/export-pdf-all`, `guide-complet-${data?.profile || "aide"}.pdf`, () => setDownloadingAll(false));
            }}
            className="inline-flex items-center gap-2 mt-5 text-xs font-semibold text-white/90 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full px-4 py-2 transition-colors disabled:opacity-60"
          >
            {downloadingAll ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            Télécharger le pack PDF complet ({guideCount} formations &amp; notices)
          </button>
        )}
      </div>

      {isLoading && <div className="text-sm text-slate-400 py-10 text-center">Chargement…</div>}

      {!isLoading && filtered.length === 0 && (
        <div data-testid="help-center-empty" className="text-sm text-slate-400 py-10 text-center border border-dashed border-slate-200 rounded-xl bg-white">
          Aucun résultat pour « {search} ».
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <div className="grid md:grid-cols-3 gap-6">
          {["training", "guide", "faq"].map((section) => {
            const items = grouped[section];
            if (items.length === 0) return null;
            const { label, icon: Icon, accent } = SECTION_META[section];
            return (
              <div key={section} data-testid={`help-section-${section}`} className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-900 pb-2 border-b-2" style={{ borderColor: accent }}>
                  <Icon size={16} style={{ color: accent }} /> {label}
                  <span className="ml-auto text-xs font-normal text-slate-400">{items.length}</span>
                </div>
                <div className="space-y-2.5">
                  {items.map((a, idx) => (
                    <ArticleCard key={a.id} article={a} defaultOpen={section !== "faq" && idx === 0} highlighted={targetSlug && a.slug === targetSlug} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
